import { Filter } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import {
  formatNumber,
  NumberFormatPreset,
  QuickViewReferenceValue,
  QuickViewWidget,
  useScrollIntoView,
} from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';
import {
  DemandRecommendationDimensions,
  DemandRecommendationMeasures,
} from '~/common/types/cubes/DemandRecommendation';
import { useDateRange } from '~/components/DateRangeProvider';
import { computeChangeRate, convertDateRangeToTimeDimension } from '~/util/cube';
import { getPrimaryValue } from '~/util/cube/getPrimaryValue';

const formatValueCompact = formatNumber(NumberFormatPreset.INTEGER);
const formatValue = formatNumber(NumberFormatPreset.INTEGER);
const formatChange = formatNumber(NumberFormatPreset.PERCENT_DECIMAL);

type DemandRecommendationsCountWidgetProps = {
  filters?: Filter[];
};

export const DemandRecommendationsCountWidget = ({ filters }: DemandRecommendationsCountWidgetProps) => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandRecommendationsCountWidget' });
  const [widgetRef, active] = useScrollIntoView({ hash: 'demand-recommendations-count-widget' });
  const { currentDateRange, referenceDateRange } = useDateRange();

  const measure = DemandRecommendationMeasures.RECOMMENDATIONS_COUNT;
  const query = {
    measures: [measure],
    filters,
  };

  const cubeQueryCurrent = useCubeQuery({
    ...query,
    timeDimensions: convertDateRangeToTimeDimension(DemandRecommendationDimensions.CREATED_AT, currentDateRange),
  });

  const cubeQueryPrevious = useCubeQuery({
    ...query,
    timeDimensions: convertDateRangeToTimeDimension(DemandRecommendationDimensions.CREATED_AT, referenceDateRange),
  });

  const currentValue = getPrimaryValue(cubeQueryCurrent.resultSet, measure) ?? 0;
  const previousValue = getPrimaryValue(cubeQueryPrevious.resultSet, measure) ?? 0;
  const changeRate = computeChangeRate(currentValue, previousValue);

  return (
    <QuickViewWidget
      ref={widgetRef}
      active={active}
      loading={cubeQueryCurrent.isLoading || cubeQueryPrevious.isLoading}
      title={t('title')}
      subTitle={t('subTitle')}
      value={formatValueCompact(currentValue)}
      valueTooltip={formatValue(currentValue)}
      data-intercom-target="DemandRecommendationsCountWidget"
      referenceValue={
        previousValue && (
          <QuickViewReferenceValue
            description={formatValueCompact(previousValue)}
            value={formatChange(changeRate)}
            dotColor={changeRate > 0 ? 'success' : 'error'}
          />
        )
      }
    />
  );
};
